export type QueryParamsType = Record<string | number, any>
export type ResponseFormat = keyof Omit<Body, 'body' | 'bodyUsed'>

export interface FullRequestParams extends Omit<RequestInit, 'body'> {
  /** set parameter to `true` for call `securityWorker` for this request */
  secure?: boolean
  /** request path */
  path: string
  /** content type of request body */
  type?: ContentType
  /** query params */
  query?: QueryParamsType
  /** format of response (i.e. response.json() -> format: "json") */
  format?: ResponseFormat
  /** request body */
  body?: unknown
  /** base url */
  baseUrl?: string
  /** request cancellation token */
  cancelToken?: CancelToken
  /** request to fake api */
  isFake?: boolean
  /** no error handlers call */
  isNoErrorHandler?: boolean
}

export type RequestParams = Omit<FullRequestParams, 'body' | 'method' | 'query' | 'path'>

export interface ApiConfig<SecurityDataType = unknown> {
  baseUrl?: string
  baseApiParams?: Omit<RequestParams, 'baseUrl' | 'cancelToken' | 'signal'>
  securityWorker?: (
    securityData: SecurityDataType | null,
  ) => Promise<RequestParams | void> | RequestParams | void
  toQueryString?: (rawQuery?: QueryParamsType) => string
}

export interface HttpResponse<D, E = unknown> extends Response {
  data: D
  error: E
}

export type HttpData<Result> =
  Result extends Promise<infer T>
    ? T extends HttpResponse<infer D>
      ? HttpResponse<D, undefined>
      : T
    : Awaited<Result>

type HttpErrorBase<Result> =
  Result extends Promise<infer T>
    ? T extends HttpResponse<unknown, infer E>
      ? HttpResponse<undefined, E | Error>
      : T
    : Awaited<Result>

type AbortError = DOMException & { readonly name: 'AbortError' }

type HandlerParamsMayAbort<Args extends any[]> = [
  Extract<Args[number], { ctrl: AbortController } | { signal: AbortSignal }>,
] extends [never]
  ? false
  : true

export type HttpError<Result, Args extends any[] = []> =
  HandlerParamsMayAbort<Args> extends true
    ? HttpErrorBase<Result> | AbortError
    : HttpErrorBase<Result>

type CancelToken = symbol | string | number

type ErrorHandler = (r: HttpResponse<unknown, unknown>) => void

export enum ContentType {
  Json = 'application/json',
  FormData = 'multipart/form-data',
  UrlEncoded = 'application/x-www-form-urlencoded',
}

export class HttpClient<SecurityDataType = unknown> {
  public baseUrl = ''
  public fakeReq: (arg: FullRequestParams) => Promise<HttpResponse<any, any>> = () =>
    new Promise((resolve) =>
      resolve({
        ...new Response(),
        data: { test: true },
        error: {},
      }),
    )
  public errorHandlers: Set<ErrorHandler> = new Set<ErrorHandler>()
  private securityData: SecurityDataType | null = null
  private securityWorker?: ApiConfig<SecurityDataType>['securityWorker']
  private abortControllers = new Map<CancelToken, AbortController>()

  public changeHeaders = (headers: HeadersInit) => {
    this.baseApiParams.headers = { ...this.baseApiParams.headers, ...headers }
  }

  private baseApiParams: RequestParams = {
    credentials: 'same-origin',
    headers: {},
    mode: 'no-cors',
    redirect: 'follow',
    referrerPolicy: 'no-referrer',
  }

  constructor(apiConfig: ApiConfig<SecurityDataType> = {}) {
    Object.assign(this, apiConfig)
  }

  private addQueryParam(query: QueryParamsType, key: string) {
    const value = query[key]

    if (Array.isArray(value)) {
      if (!value.length) return undefined

      return `${encodeURIComponent(key)}=${value
        .map((v: any) => encodeURIComponent(v))
        .join(`&${key}=`)}`
    }

    return `${encodeURIComponent(key)}=${encodeURIComponent(value.toString())}`
  }

  protected toQueryString(rawQuery?: QueryParamsType): string {
    const query = rawQuery || {}
    const keys = Object.keys(query).filter((key) => 'undefined' !== typeof query[key])

    return keys
      .map((key) =>
        typeof query[key] === 'object' && !Array.isArray(query[key])
          ? this.toQueryString(query[key] as QueryParamsType)
          : this.addQueryParam(query, key),
      )
      .filter((v: any) => v)
      .join('&')
  }

  private contentFormatters: Record<ContentType, (input: any) => any> = {
    [ContentType.Json]: (input: any) =>
      input !== null && (typeof input === 'object' || typeof input === 'string')
        ? JSON.stringify(input)
        : input,
    [ContentType.FormData]: (input: any) =>
      Object.keys(input || {}).reduce((data, key) => {
        if (Array.isArray(input[key])) {
          input[key].forEach((v: any) => {
            data.append(key, v)
          })
        } else {
          data.append(key, input[key])
        }

        return data
      }, new FormData()),
    [ContentType.UrlEncoded]: (input: any) => this.toQueryString(input),
  }

  private mergeRequestParams(params1: RequestParams, params2?: RequestParams): RequestParams {
    return {
      ...this.baseApiParams,
      ...params1,
      ...(params2 || {}),
      headers: {
        ...(this.baseApiParams.headers || {}),
        ...(params1.headers || {}),
        ...((params2 && params2.headers) || {}),
      },
    }
  }

  public request = async <T = any, E = any>({
    body,
    secure,
    path,
    type,
    query,
    format = 'json',
    baseUrl,
    cancelToken,
    isFake = false,
    ...params
  }: FullRequestParams): Promise<HttpResponse<T, E>> => {
    const secureParams =
      (this.securityWorker && (await this.securityWorker(this.securityData))) || {}
    const requestParams = this.mergeRequestParams(params, secureParams)
    const queryString = query && this.toQueryString(query)
    const payloadFormatter = this.contentFormatters[type || ContentType.Json]

    if (isFake) {
      return this.fakeReq({
        body,
        secure,
        path,
        type,
        query,
        format,
        baseUrl,
        cancelToken,
        isFake,
        ...params,
      })
    }

    return fetch(`${baseUrl || this.baseUrl || ''}${path}${queryString ? `?${queryString}` : ''}`, {
      ...requestParams,
      headers: {
        ...(type && type !== ContentType.FormData ? { 'Content-Type': type } : {}),
        ...(requestParams.headers || {}),
      },
      body: typeof body === 'undefined' || body === null ? null : payloadFormatter(body),
    }).then(async (response) => {
      const r = response as HttpResponse<T, E>

      Object.defineProperty(r, 'data', { value: null, writable: true })
      Object.defineProperty(r, 'error', { value: null, writable: true })

      const data = await response[format]()
        .then((data) => {
          if (r.ok) {
            r.data = data
          } else {
            r.error = data
            if (!requestParams.isNoErrorHandler) this.errorHandlers.forEach((handler) => handler(r))
          }

          return r
        })
        .catch((e) => {
          r.error = e
          if (!requestParams.isNoErrorHandler) this.errorHandlers.forEach((handler) => handler(r))
          return r
        })

      if (cancelToken) {
        this.abortControllers.delete(cancelToken)
      }

      if (!response.ok) throw data
      return data
    })
  }
}
